import { triniumApi } from '../../slice';
import { BoardQuery } from '../../types';
import { toBoardQueryBody, transformBoardQueryResponse, withForcedPins } from './types';

export const queriesSlice = triniumApi.injectEndpoints({
  endpoints(builder) {
    return {
      fetchBoard: builder.query<BoardQuery[], string>({
        query: (terminalNumber) => `${terminalNumber}/boardqueries`,
        transformResponse: transformBoardQueryResponse
      }),

      setFavoriteQuery: builder.mutation<BoardQuery, { terminalNumber: string; id: number; isFavorite: boolean }>({
        query: (params) => ({
          url: `${params.terminalNumber}/boardqueries/${String(params.id)}`,
          method: 'PUT',
          body: {
            action: 'setFavorability',
            data: {
              isFavorite: params.isFavorite
            }
          }
        }),
        async onQueryStarted(params, { dispatch, queryFulfilled }) {
          const optimisticUpdate = dispatch(
            queriesSlice.util.updateQueryData('fetchBoard', params.terminalNumber, (draft) => {
              const query = draft.find((q) => q.id === params.id);
              if (query) {
                Object.assign(query, {
                  isFavorite: params.isFavorite
                });
              }
            })
          );

          try {
            await queryFulfilled;
          } catch {
            optimisticUpdate.undo();
          }
        }
      }),

      setDefaultQuery: builder.mutation<BoardQuery, { terminalNumber: string; id: number; isDefault: boolean }>({
        query: (params) => ({
          url: `${params.terminalNumber}/boardqueries/${String(params.id)}`,
          method: 'PUT',
          body: {
            action: 'setDefault',
            data: {
              isDefault: params.isDefault
            }
          }
        }),
        async onQueryStarted(params, { dispatch, queryFulfilled }) {
          const optimisticUpdate = dispatch(
            queriesSlice.util.updateQueryData('fetchBoard', params.terminalNumber, (draft) => {
              const prevDefaultQuery = draft.find((q) => q.isDefault);

              if (prevDefaultQuery) {
                Object.assign(prevDefaultQuery, {
                  isDefault: false
                });
              }

              const query = draft.find((q) => q.id === params.id);

              if (query) {
                Object.assign(query, {
                  isDefault: params.isDefault
                });
              }
            })
          );

          try {
            await queryFulfilled;
          } catch {
            optimisticUpdate.undo();
          }
        }
      }),

      createBoardQuery: builder.mutation<BoardQuery, { terminalNumber: string; query: Pick<BoardQuery, 'name' | 'configuration'> }>({
        query: (params) => ({
          url: `${params.terminalNumber}/boardqueries`,
          method: 'POST',
          // The server starts a new record as inactive, so a create that left the flag out would land off the board
          body: toBoardQueryBody({ ...params.query, isActive: true })
        }),
        // Same omitted-false rule as fetchBoard's transform, since the created query joins that cache from this response
        transformResponse: (query: BoardQuery) => ({ ...query, isActive: query.isActive ?? false }),
        async onQueryStarted(params, { dispatch, queryFulfilled }) {
          try {
            // The id is the server's to issue, so the query joins the cache once it has one rather than optimistically
            const { data: created } = await queryFulfilled;

            dispatch(
              queriesSlice.util.updateQueryData('fetchBoard', params.terminalNumber, (cache) => {
                cache.push(withForcedPins(created));
              })
            );
          } catch {
            // Nothing reached the cache, so a failed create has nothing to roll back
          }
        }
      }),

      updateBoardQuery: builder.mutation<BoardQuery, { terminalNumber: string; query: BoardQuery }>({
        query: (params) => ({
          url: `${params.terminalNumber}/boardqueries/${String(params.query.id)}`,
          method: 'PATCH',
          body: toBoardQueryBody(params.query)
        }),
        async onQueryStarted(params, { dispatch, queryFulfilled }) {
          const optimisticUpdate = dispatch(
            queriesSlice.util.updateQueryData('fetchBoard', params.terminalNumber, (draft) => {
              const query = draft.find((q) => q.id === params.query.id);

              if (query) {
                Object.assign(query, {
                  name: params.query.name,
                  configuration: params.query.configuration,
                  isActive: params.query.isActive
                });
              }
            })
          );

          try {
            await queryFulfilled;
          } catch {
            optimisticUpdate.undo();
          }
        }
      })
    };
  }
});

export const {
  useFetchBoardQuery,
  useSetFavoriteQueryMutation,
  useSetDefaultQueryMutation,
  useCreateBoardQueryMutation,
  useUpdateBoardQueryMutation
} = queriesSlice;
